import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Checkbox as C, Switch as S, RadioGroup as R } from 'radix-ui';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

const box =
  'peer inline-flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border border-control-border bg-surface ' +
  'text-on-primary transition-colors duration-150 hover:border-primary/60 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ' +
  'data-[state=checked]:border-primary data-[state=checked]:bg-primary ' +
  'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary ' +
  'disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-negative';

export const Checkbox = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof C.Root> & { label?: ReactNode; description?: ReactNode }
>(function Checkbox({ className, label, description, id, ...rest }, ref) {
  const el = (
    <C.Root ref={ref} id={id} className={cn(box, !label && className)} {...rest}>
      <C.Indicator className="data-[state=checked]:animate-[gh-fade_120ms_ease-out]">
        {rest.checked === 'indeterminate' ? (
          <MinusIcon weight="bold" className="size-3" />
        ) : (
          <CheckIcon weight="bold" className="size-3" />
        )}
      </C.Indicator>
    </C.Root>
  );
  if (!label) return el;
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2.5 text-[0.875rem]',
        rest.disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="pt-0.5">{el}</span>
      <span className="grid gap-0.5">
        <span className="text-ink">{label}</span>
        {description && <span className="text-sm text-ink-muted">{description}</span>}
      </span>
    </label>
  );
});

export const Switch = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof S.Root> & { label?: ReactNode; description?: ReactNode }
>(function Switch({ className, label, description, ...rest }, ref) {
  const el = (
    <S.Root
      ref={ref}
      className={cn(
        'group relative inline-flex h-[22px] w-[38px] shrink-0 items-center rounded-full border border-control-border bg-sunken p-0.5',
        'transition-colors duration-200 data-[state=checked]:border-primary data-[state=checked]:bg-primary',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:opacity-50',
        !label && className,
      )}
      {...rest}
    >
      <S.Thumb
        className={cn(
          // Off: ink-muted thumb so the state reads at ≥3:1 against the track in both themes.
          'block size-4 rounded-full bg-ink-muted shadow-[0_1px_2px_rgb(4_18_34/0.25)] transition-[transform,background-color] duration-200 ease-[var(--gh-ease-spring)]',
          'data-[state=checked]:translate-x-4 data-[state=checked]:bg-surface',
        )}
      />
    </S.Root>
  );
  if (!label) return el;
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start justify-between gap-4 text-[0.875rem]',
        className,
      )}
    >
      <span className="grid gap-0.5">
        <span className="text-ink">{label}</span>
        {description && <span className="text-sm text-ink-muted">{description}</span>}
      </span>
      {el}
    </label>
  );
});

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

/** Radio group. `variant="cards"` renders bordered choice cards (plans, channels). */
export function RadioGroup({
  options,
  variant = 'list',
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof R.Root> & {
  options: RadioOption[];
  variant?: 'list' | 'cards';
}) {
  return (
    <R.Root
      className={cn(
        variant === 'cards' ? 'grid gap-2.5 sm:grid-flow-col sm:auto-cols-fr' : 'grid gap-2.5',
        className,
      )}
      {...rest}
    >
      {options.map((o) => (
        <label
          key={o.value}
          className={cn(
            'group flex cursor-pointer items-start gap-2.5 text-[0.875rem]',
            variant === 'cards' &&
              'rounded-md border border-hairline-strong bg-surface p-3.5 transition-colors hover:border-ink-subtle/60 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary-soft/50 has-[[data-state=checked]]:shadow-[0_0_0_1px_var(--gh-primary)]',
            o.disabled && 'cursor-not-allowed opacity-55',
          )}
        >
          <R.Item
            value={o.value}
            disabled={o.disabled}
            className="mt-0.5 inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border border-control-border bg-surface transition-colors hover:border-primary/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus data-[state=checked]:border-primary"
          >
            <R.Indicator className="size-2.5 rounded-full bg-primary" />
          </R.Item>
          <span className="grid gap-0.5">
            <span className="font-medium text-ink">{o.label}</span>
            {o.description && <span className="text-sm text-ink-muted">{o.description}</span>}
          </span>
        </label>
      ))}
    </R.Root>
  );
}
